import { useEffect, useState, useRef, useMemo } from 'react';
import { 
  Package, Plus, Search, LayoutGrid, List, Trash2, Edit2, 
  ChevronRight, X, Image as ImageIcon, Check, RefreshCcw, 
  Eye, EyeOff, Layers, ExternalLink, Tag, FileText, 
  Upload, Star, Smartphone, Settings
} from 'lucide-react';
import '../../styles/admin.css';
import { fetchProductsAdmin, createProduct, updateProduct, deleteProduct } from '../../lib/products';
import { fetchCategories } from '../../lib/categories';
import { getFileUrl, pb } from '../../lib/pb';

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState('create');
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const sentinelRef = useRef(null);

  async function load(targetPage = 1) {
    setError('');
    const isFirst = targetPage === 1;
    if (isFirst) { setLoading(true); setItems([]); } else { setLoadingMore(true); }
    try {
      const res = await fetchProductsAdmin(targetPage, 24, search);
      setTotalPages(res.totalPages || 1);
      if (isFirst) setItems(res.items || []);
      else setItems(prev => [...prev, ...(res.items || [])]);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(isFirst ? false : loading);
      setLoadingMore(false);
    }
  }

  useEffect(() => { load(1); setPage(1); }, [search]);
  useEffect(() => { if (page > 1) load(page); }, [page]);

  useEffect(() => {
    fetchCategories('catalogue').then(setCategories).catch(() => {});
  }, []);

  // Infinite scroll
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || loading || loadingMore || page >= totalPages) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setPage(p => p + 1);
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loading, loadingMore, page, totalPages]);

  const filteredItems = useMemo(() => {
    return items.filter(p => {
      if (filterCategory && p.category !== filterCategory) return false;
      if (filterStatus === 'published' && !p.published) return false;
      if (filterStatus === 'draft' && p.published) return false;
      return true;
    });
  }, [items, filterCategory, filterStatus]);

  const openCreate = () => { setMode('create'); setEditing({ title: '', slug: '', category: '', price: null, published: true, images: [] }); };
  const openEdit = (p) => { setMode('edit'); setEditing(p); };

  async function handleTogglePublished(p) {
    try { await updateProduct(p.id, { published: !p.published }); load(1); } catch (e) {}
  }

  return (
    <div className="products-page">
      {/* HEADER SECTION */}
      <header className="prod-header">
        <div className="header-text">
          <h1>Catalogue Produits</h1>
          <p className="muted">Gérez vos articles, tarifs et fiches techniques.</p>
        </div>
        <button className="btn-add-main" onClick={openCreate}>
          <Plus size={20} />
          <span>Nouveau Produit</span>
        </button>
      </header>

      {/* FILTERS BAR */}
      <div className="prod-filters">
        <div className="search-box">
          <Search size={18} />
          <input placeholder="Titre, référence..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <div className="filter-group">
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="">Toutes les catégories</option>
            {categories.filter(c => !c.parent).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">Tous les statuts</option>
            <option value="published">Publiés</option>
            <option value="draft">Brouillons</option>
          </select>
        </div>

        <div className="view-switcher">
           <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={18}/></button>
           <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List size={18}/></button>
        </div>
      </div>

      {error && <div className="error-pill">{error}</div>}

      {/* MAIN CONTENT */}
      <main className="prod-main">
        {loading && items.length === 0 ? (
          <div className="loading-state">Initialisation du catalogue...</div>
        ) : (
          <div className={`prod-content-view ${viewMode}`}>
            {viewMode === 'grid' ? (
              <div className="prod-grid">
                {filteredItems.map(p => (
                  <div key={p.id} className="prod-card-premium" onClick={() => openEdit(p)}>
                    <div className="prod-card-img">
                      {p.images && p.images[0] ? (
                        <img src={getFileUrl(p, p.images[0])} alt={p.title} />
                      ) : (
                        <div className="no-img"><ImageIcon size={32}/></div>
                      )}
                      <div className={`prod-card-badge ${p.published ? 'ok' : 'none'}`}>
                        {p.published ? <Eye size={12}/> : <EyeOff size={12}/>}
                      </div>
                      {p.price && <div className="prod-card-price">{p.price}€</div>}
                    </div>
                    <div className="prod-card-info">
                       <span className="cat-tag">{p.expand?.category?.name || 'Général'}</span>
                       <h3>{p.title}</h3>
                       <div className="prod-card-footer">
                          <div className="stats">
                             {p.images?.length > 1 && <span><ImageIcon size={12}/> {p.images.length}</span>}
                             {p.technical_sheet && <span className="tech"><FileText size={12}/> OK</span>}
                          </div>
                          <ChevronRight size={16} className="arrow" />
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="prod-list">
                {filteredItems.map(p => (
                  <div key={p.id} className="prod-list-row" onClick={() => openEdit(p)}>
                    <div className="row-img">
                       {p.images?.[0] ? <img src={getFileUrl(p, p.images[0])} /> : <ImageIcon size={14}/>}
                    </div>
                    <div className="row-main">
                       <span className="title">{p.title}</span>
                       <span className="sub">{p.expand?.category?.name} • {p.slug}</span>
                    </div>
                    <div className="row-price">{p.price ? `${p.price}€` : '—'}</div>
                    <div className={`row-status ${p.published ? 'ok' : ''}`}>
                       {p.published ? <Eye size={16}/> : <EyeOff size={16}/>}
                    </div>
                    <ChevronRight size={18} className="row-arrow" />
                  </div>
                ))}
              </div>
            )}
            
            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
               {loadingMore && <div className="loading-more">Chargement de la suite...</div>}
            </div>
          </div>
        )}
      </main>

      {editing && (
        <ProductModal 
          mode={mode} 
          product={editing} 
          categories={categories}
          onClose={() => setEditing(null)} 
          onSaved={() => { setEditing(null); load(1); }} 
        />
      )}

      <style>{`
        .products-page { display: flex; flex-direction: column; gap: 20px; animation: fadeIn 0.4s ease; padding-bottom: 80px; }
        
        .prod-header { display: flex; justify-content: space-between; align-items: flex-end; }
        .prod-header h1 { font-size: 28px; font-weight: 900; margin: 0; color: #0f172a; }
        
        .btn-add-main { background: var(--admin-primary); border: none; font-weight: 800; border-radius: 16px; padding: 12px 20px; display: flex; align-items: center; gap: 10px; cursor: pointer; box-shadow: 0 10px 15px -3px rgba(234, 179, 8, 0.2); transition: all 0.2s; }

        .prod-filters { display: flex; gap: 15px; align-items: center; background: #fff; padding: 10px 15px; border-radius: 20px; border: 1px solid var(--admin-border); flex-wrap: wrap; }
        
        .search-box { flex: 1; display: flex; align-items: center; gap: 10px; padding: 0 10px; color: #94a3b8; min-width: 200px; }
        .search-box input { border: none; outline: none; width: 100%; font-size: 14px; font-weight: 600; color: #0f172a; background: transparent; }

        .filter-group { display: flex; gap: 8px; }
        .filter-group select { background: #f1f5f9; border: none; border-radius: 12px; padding: 8px 15px; font-weight: 800; font-size: 13px; color: #0f172a; outline: none; }

        .view-switcher { display: flex; background: #f1f5f9; padding: 4px; border-radius: 12px; gap: 4px; }
        .view-switcher button { width: 36px; height: 36px; border-radius: 9px; border: none; background: none; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .view-switcher button.active { background: #fff; color: #0f172a; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

        /* GRID */
        .prod-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px; }
        .prod-card-premium { background: #fff; border: 1px solid var(--admin-border); border-radius: 28px; overflow: hidden; cursor: pointer; transition: all 0.3s; }
        .prod-card-premium:hover { transform: translateY(-5px); border-color: var(--admin-primary); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.05); }
        
        .prod-card-img { height: 200px; background: #f8fafc; position: relative; overflow: hidden; }
        .prod-card-img img { width: 100%; height: 100%; object-fit: cover; }
        .no-img { height: 100%; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
        
        .prod-card-badge { position: absolute; top: 12px; right: 12px; width: 28px; height: 28px; border-radius: 50%; background: rgba(255,255,255,0.9); display: flex; align-items: center; justify-content: center; }
        .prod-card-badge.ok { color: #10b981; }
        .prod-card-price { position: absolute; bottom: 12px; left: 12px; background: #0f172a; color: #fff; padding: 4px 10px; border-radius: 10px; font-weight: 800; font-size: 14px; }

        .prod-card-info { padding: 20px; }
        .cat-tag { font-size: 10px; font-weight: 900; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
        .prod-card-info h3 { margin: 4px 0 15px 0; font-size: 17px; font-weight: 800; color: #0f172a; line-height: 1.3; }
        
        .prod-card-footer { display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 15px; }
        .stats { display: flex; gap: 10px; color: #94a3b8; font-size: 11px; font-weight: 800; }
        .stats span { display: flex; align-items: center; gap: 4px; }
        .arrow { color: #f1f5f9; transition: transform 0.2s; }
        .prod-card-premium:hover .arrow { color: var(--admin-primary); transform: translateX(3px); }

        /* LIST */
        .prod-list { background: #fff; border: 1px solid var(--admin-border); border-radius: 28px; overflow: hidden; }
        .prod-list-row { display: flex; align-items: center; gap: 15px; padding: 12px 20px; border-bottom: 1px solid #f8fafc; cursor: pointer; transition: background 0.2s; }
        .prod-list-row:hover { background: #f8fafc; }
        .row-img { width: 44px; height: 32px; background: #f1f5f9; border-radius: 8px; overflow: hidden; display: flex; align-items: center; justify-content: center; }
        .row-img img { width: 100%; height: 100%; object-fit: cover; }
        .row-main { flex: 1; display: flex; flex-direction: column; }
        .row-main .title { font-weight: 800; font-size: 15px; color: #0f172a; }
        .row-main .sub { font-size: 11px; font-weight: 700; color: #94a3b8; }
        .row-price { font-weight: 800; color: #0f172a; min-width: 60px; text-align: right; }
        .row-status { color: #cbd5e1; }
        .row-status.ok { color: #10b981; }
        .row-arrow { color: #f1f5f9; }

        @media (max-width: 900px) {
          .prod-header { flex-direction: column; align-items: flex-start; gap: 15px; }
          .prod-filters { gap: 10px; }
          .search-box { order: 1; width: 100%; }
          .filter-group { order: 2; width: 100%; }
          .filter-group select { flex: 1; }
          .view-switcher { display: none; }
          .prod-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}

function ProductModal({ mode, product, categories, onClose, onSaved }) {
    const [tab, setTab] = useState('infos');
    const [saving, setSaving] = useState(false);
    
    // State
    const [title, setTitle] = useState(product.title || '');
    const [slug, setSlug] = useState(product.slug || '');
    const [category, setCategory] = useState(product.category || '');
    const [price, setPrice] = useState(product.price || '');
    const [excerpt, setExcerpt] = useState(product.excerpt || '');
    const [description, setDescription] = useState(product.description || '');
    const [published, setPublished] = useState(product.published ?? true);

    // Images & Files
    const [newImages, setNewImages] = useState([]);
    const [previews, setPreviews] = useState(product.images?.map(img => getFileUrl(product, img)) || []);
    const [techFile, setTechFile] = useState(null);
    const techInputRef = useRef(null);

    const slugify = (v) => v.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    const handleSave = async (e) => {
        e.preventDefault(); setSaving(true);
        const fd = new FormData();
        fd.append('title', title); fd.append('slug', slug); fd.append('category', category);
        fd.append('price', price || 0); fd.append('excerpt', excerpt); fd.append('description', description);
        fd.append('published', published);
        
        newImages.forEach(file => fd.append('images', file));
        if (techFile) fd.append('technical_sheet', techFile);

        try {
            if (product.id) await updateProduct(product.id, fd);
            else await createProduct(fd);
            onSaved();
        } catch(e) { alert(e.message); } finally { setSaving(false); }
    };

    return (
        <div className="modal-overlay-s" onClick={e => e.target.className === 'modal-overlay-s' && onClose()}>
            <div className="modal-content-l">
                <div className="modal-s-top">
                    <div className="modal-s-tabs">
                        <button className={tab === 'infos' ? 'active' : ''} onClick={() => setTab('infos')}>Général</button>
                        <button className={tab === 'media' ? 'active' : ''} onClick={() => setTab('media')}>Visuals</button>
                        <button className={tab === 'tech' ? 'active' : ''} onClick={() => setTab('tech')}>Technique</button>
                    </div>
                    <button className="s-close" onClick={onClose}><X size={20}/></button>
                </div>
                
                <form className="modal-s-body" onSubmit={handleSave}>
                    {tab === 'infos' ? (
                        <div className="s-form">
                            <div className="s-field">
                                <label>Titre du produit</label>
                                <input value={title} onChange={e => { setTitle(e.target.value); if(!product.slug) setSlug(slugify(e.target.value)); }} required />
                            </div>
                            <div className="s-field">
                                <label>Catégorie principale</label>
                                <select value={category} onChange={e => setCategory(e.target.value)} required>
                                    <option value="">Choisir...</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="s-field">
                                <label>Résumé court</label>
                                <textarea value={excerpt} onChange={e => setExcerpt(e.target.value)} style={{height: 60}} />
                            </div>
                            <div className="s-field">
                                <label>Description complète</label>
                                <textarea value={description} onChange={e => setDescription(e.target.value)} style={{height: 120}} />
                            </div>
                        </div>
                    ) : tab === 'media' ? (
                        <div className="s-media-section">
                             <div className="image-grid-manager">
                                {previews.map((src, i) => (
                                    <div key={i} className="img-p-preview">
                                        <img src={src} />
                                        <button type="button" className="img-del" onClick={() => setPreviews(prev => prev.filter((_, idx) => idx !== i))}>×</button>
                                    </div>
                                ))}
                                <label className="img-add-box">
                                    <Plus size={24}/>
                                    <input type="file" multiple hidden accept="image/*" onChange={e => {
                                        const files = Array.from(e.target.files);
                                        setNewImages(prev => [...prev, ...files]);
                                        setPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
                                    }} />
                                </label>
                             </div>
                             <p className="muted" style={{fontSize: 12, marginTop: 15}}>Glissez-déposez pour réorganiser l'ordre (Prochaine mise à jour).</p>
                        </div>
                    ) : (
                        <div className="s-form">
                             <div className="s-field">
                                <label>Prix indicatif (€)</label>
                                <input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} />
                             </div>
                             <div className="s-field">
                                <label>Fiche Technique (PDF)</label>
                                <div className="pdf-upload-box" onClick={() => techInputRef.current.click()}>
                                    <FileText size={24} className={techFile || product.technical_sheet ? 'ok' : ''} />
                                    <span>{techFile ? techFile.name : (product.technical_sheet ? 'Fiche existante' : 'Cliquer pour uploader')}</span>
                                    <input ref={techInputRef} type="file" hidden accept="application/pdf" onChange={e => setTechFile(e.target.files[0])} />
                                </div>
                             </div>
                             <div className="s-toggle">
                                <label>Publié sur le catalogue</label>
                                <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
                            </div>
                        </div>
                    )}
                    
                    <div className="modal-s-footer">
                        {product.id && <button type="button" className="btn-del-team" onClick={async () => { if(window.confirm('Supprimer ce produit ?')){ await deleteProduct(product.id); onSaved(); }}}><Trash2 size={16}/></button>}
                        <div className="spacer"></div>
                        <button type="submit" className="btn-submit-team" disabled={saving}>{saving ? '...' : 'Finaliser le Produit'}</button>
                    </div>
                </form>
            </div>
            <style>{`
                .modal-overlay-s { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(10px); z-index: 20000; display: flex; align-items: center; justify-content: center; padding: 20px; }
                .modal-content-l { background: #fff; width: 100%; max-width: 600px; border-radius: 32px; overflow: hidden; box-shadow: 0 30px 60px -12px rgba(0,0,0,0.3); }
                .modal-s-top { padding: 20px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; }
                .modal-s-tabs { display: flex; gap: 5px; background: #f1f5f9; padding: 4px; border-radius: 12px; }
                .modal-s-tabs button { border: none; background: none; padding: 8px 18px; border-radius: 9px; font-weight: 800; font-size: 13px; color: #64748b; cursor: pointer; }
                .modal-s-tabs button.active { background: #fff; color: #0f172a; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }

                .modal-s-body { padding: 25px; max-height: 70vh; overflow-y: auto; }
                .s-form { display: grid; gap: 20px; }
                .s-field { display: grid; gap: 8px; }
                .s-field label { font-size: 11px; font-weight: 900; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
                .s-field input, .s-field select, .s-field textarea { background: #f8fafc; border: 1.5px solid #e2e8f0; padding: 12px 15px; border-radius: 16px; font-weight: 700; outline: none; transition: border 0.2s; }
                .s-field input:focus { border-color: var(--admin-primary); }
                
                .image-grid-manager { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
                .img-p-preview { aspect-ratio: 1; border-radius: 12px; overflow: hidden; position: relative; }
                .img-p-preview img { width: 100%; height: 100%; object-fit: cover; }
                .img-del { position: absolute; top: 5px; right: 5px; background: rgba(0,0,0,0.5); color: #fff; border: none; width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; }
                .img-add-box { aspect-ratio: 1; border: 2px dashed #e2e8f0; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #cbd5e1; cursor: pointer; transition: all 0.2s; }
                .img-add-box:hover { background: #f8fafc; border-color: var(--admin-primary); color: var(--admin-primary); }

                .pdf-upload-box { border: 2px dashed #e2e8f0; padding: 20px; border-radius: 16px; display: flex; flex-direction: column; align-items: center; gap: 10px; cursor: pointer; }
                .pdf-upload-box svg.ok { color: #10b981; }
                .pdf-upload-box span { font-size: 13px; font-weight: 800; color: #64748b; }

                .modal-s-footer { display: flex; align-items: center; gap: 15px; margin-top: 25px; border-top: 1px solid #f1f5f9; padding-top: 20px; }
                .btn-del-team { width: 44px; height: 44px; border-radius: 14px; background: #fff1f2; color: #f43f5e; border: none; cursor: pointer; }
                .btn-submit-team { flex: 1; height: 50px; background: #0f172a; color: #fff; border: none; border-radius: 16px; font-weight: 800; cursor: pointer; letter-spacing: 0.5px; }
                .spacer { flex: 1; }
                .s-toggle { display: flex; justify-content: space-between; align-items: center; background: #f8fafc; padding: 15px; border-radius: 16px; }
                .s-toggle label { font-size: 14px; font-weight: 800; }
            `}</style>
        </div>
    );
}
